import { Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { toggleFavorite } from 'stores/match';
import * as _ from 'lodash';
import { AWS_BUCKET_URL } from 'toolkit/constants';
import {
  gameDateToMatchDay,
  gameDateToMatchTime,
  getMatchTime,
  checkHasMarketVariable,
  sportTypeToSportId,
  isMaradonaSpecifier
} from 'toolkit/utils.js';
import {
  EnumMarketName,
  EnumEventStatus,
  EnumMatchStatusName,
  EnumMarketStatus
} from 'toolkit/Enums.js';
import { getTeamNameHelper } from 'helper/matchDetailHelper';
import BetTitleAndBox from './bet_title_and_box.js';
import Jersey from 'components/jersey';
import { useMediaQuery } from 'react-responsive';
import { showToaster } from 'mixin/general';

function MatchCard(props) {
  const { t, i18n } = useTranslation('translations', { useSuspense: false });
  const dispatch = useDispatch();
  const { providerId } = props;
  const isDesktop = useMediaQuery({
    query: '(min-width: 992px)'
  });
  if (!providerId) return null;
  const selectedLocale = i18n.language;
  const locales = useSelector((state) => state.match.locales);
  const match = useSelector((state) => state.match.events[providerId]);
  if (!match || match.isMatch === false) return null;
  const activeMarkets = match.markets.filter((x) => x.marketStatus === EnumMarketStatus.ACTIVE);

  const defaultMarketObject = {
    1: ['1', 'X', '2'],
    7: ['1', 'X', '2'],
    8: ['1', 'X', '2'],
    10: ['1X', '12', 'X2'],
    14: ['1', 'X', '2'],
    18: ['+', '-'],
    29: ['Yes', 'No'],
    186: ['1', '2']
  };

  const sortByMarketId = (arr) => {
    return _.orderBy(arr, (market) => {
      if (market === null || market === undefined) return false;
      const marketVariable = checkHasMarketVariable(market.id, market.stringId);
      if (marketVariable) {
        if (!isNaN(Number(marketVariable))) return Number(marketVariable);
        return marketVariable;
      }
      return false;
    });
  };

  // For some marketIds (notably 7/8), provider can send the same market twice:
  // one INACTIVE (marketStatus=5) and one ACTIVE (marketStatus=6).
  // When that happens, keep the non-INACTIVE ones.
  const removeInactiveIfDuplicated = (arr) => {
    if (!Array.isArray(arr) || arr.length <= 1) return arr;
    const nonInactive = arr.filter((m) => m?.marketStatus !== 5);
    return nonInactive.length > 0 ? nonInactive : arr;
  };

  const getDefaultMarket = () => {
    // hide bettitle if match card doesn't have the selected market
    if (props.hideIfNoMarket) {
      return null;
    }
    let defaultMarket;
    const winnerBet = match.markets.find((m) => m.id === 1);
    if (winnerBet) {
      defaultMarket = winnerBet;
    } else {
      const otherWinningIds = [7, 113, 114, 186, 219, 340, 251];
      defaultMarket = match.markets.find((m) => otherWinningIds.includes(m.id));
    }
    if (!defaultMarket) {
      defaultMarket = {
        id: 186,
        name: '',
        outcomeType: '',
        stringId: '',
        outcomes: defaultMarketObject[186],
        renderNull: true
      };
    }
    return defaultMarket;
  };

  function MatchCardGetTitle() {
    const sportType = match.sport;
    const providerId = match.providerId;
    let marketType = props.marketType;
    let markets = [];
    if (!match.markets || match.markets.length === 0) {
      return (
        <div className="d-flex justify-content-center align-item-center">
          {t('components.matchcard.noMarket')}
        </div>
      );
    } else if (sportType === 0) {
      if (
        match.matchStatusId == EnumMatchStatusName.FIRST_EXTRA ||
        match.matchStatusId == EnumMatchStatusName.SECOND_EXTRA ||
        match.matchStatusId === EnumMatchStatusName.AWAITING_EXTRA_TIME ||
        match.matchStatusId === EnumMatchStatusName.EXTRA_TIME_HALFTIME
      ) {
        if (marketType === 7 && match.markets.find((x) => x.id === 114)) {
          marketType = 114;
        } else if (marketType === 1 && match.markets.find((x) => x.id === 113)) {
          marketType = 113;
        } else if (marketType === 8 && match.markets.find((x) => x.id === 115)) {
          marketType = 115;
        } else if (marketType === 18 && match.markets.find((x) => x.id === 116)) {
          marketType = 116;
        }
      }

      const filteredMarkets = match.markets.filter((x) => x.id === marketType);
      const cleanedMarkets =
        marketType === 7 || marketType === 8
          ? removeInactiveIfDuplicated(filteredMarkets)
          : filteredMarkets;
      if (cleanedMarkets.length === 0) {
        if (props.lockIfNoMarket) {
          markets.push({
            id: marketType,
            name: '',
            outcomeType: '',
            stringId: '',
            outcomes: defaultMarketObject[marketType],
            renderNull: true
          });
        } else {
          markets.push({
            id: marketType,
            name: '',
            outcomeType: '',
            stringId: '',
            outcomes: defaultMarketObject[marketType],
            renderNull: true
          });
        }
      } else {
        markets.push(...cleanedMarkets);
      }
    } else {
      markets.push(getDefaultMarket());
    }

    markets = markets.sort(
      (t1, t2) => Number(t1.stringId.split('-S:')[1]) - Number(t2.stringId.split('-S:')[1])
    );

    if (marketType === 14) {
      markets = sortByMarketId(markets);
    }

    // For Maradona Handicap, filter to only maradona-level specifiers (one side=0, other>=5)
    // then pick the last one (largest specifier). This prevents regular handicap lines
    // (e.g., 1:0) from being shown instead of maradona lines (e.g., 0:9, 9:0).
    if (props.isMaradonaHandicap && markets.length > 0) {
      const maradonaMarkets = markets.filter((m) => isMaradonaSpecifier(m.stringId));
      markets = maradonaMarkets.length > 0 ? [maradonaMarkets[maradonaMarkets.length - 1]] : [markets[markets.length - 1]];
    }

    // Check if market type needs specifier (O/U and Handicap)
    const marketsWithSpecifier = [
      EnumMarketName.TOTAL_18,
      EnumMarketName.HANDICAP_14,
      EnumMarketName.FIRSTHALFOVERUNDER_68,
      EnumMarketName.FIRSTHALFHANDICAP_65
    ];
    const showSpecifier = marketsWithSpecifier.includes(marketType);
    return markets.map((market, index) => (
      <BetTitleAndBox
        key={index}
        market={market}
        matchId={providerId}
        sportType={sportType}
        lockIfNoMarket={props.lockIfNoMarket}
        hideMarketNameInCard={true}
        marketCount={activeMarkets.length}
        isLastMarket={index === markets.length - 1}
        showSpecifier={showSpecifier}
        isMaradonaHandicap={props.isMaradonaHandicap}
      />
    ));
  }

  // if (!getDefaultMarket()) {
  //   return <div></div>;
  // }

  const servingBall =
    match.sport == sportTypeToSportId('Volleyball') ? (
      <img className="serving-ball" src={`${AWS_BUCKET_URL}/Icons/182.png`} />
    ) : (
      <img className="serving-ball" src={`${AWS_BUCKET_URL}/Icons/118.png`} />
    );
  const homeScores = [];
  const awayScores = [];
  const periodScoresLenght = match.periodScores.length;
  match.periodScores.forEach((element, index) => {
    if (index === periodScoresLenght - 1) {
      homeScores.push(
        <span className="text-success" key={'homeScore1' + index}>
          {element.homeScore}
        </span>
      );
      awayScores.push(
        <span className="text-success" key={'awayScore1' + index}>
          {element.awayScore}
        </span>
      );
    } else {
      homeScores.push(<span key={'homeScore2' + index}>{element.homeScore} </span>);
      awayScores.push(<span key={'awayScore2' + index}>{element.awayScore} </span>);
    }
  });

  const homeRedCardCount = (
    <span className="badge bg-danger ms-2 px-1">{match.homeRedCardCount}</span>
  );
  const awayRedCardCount = (
    <span className="badge bg-danger ms-2 px-1">{match.awayRedCardCount}</span>
  );
  const getTeam = (team) => {
    const lastPeriodScore = match.periodScores[match.periodScores.length - 1];
    const homeServingBall =
      lastPeriodScore && lastPeriodScore.homeScore > lastPeriodScore.awayScore;
    const awayShowServingBall =
      lastPeriodScore && lastPeriodScore.homeScore <= lastPeriodScore.awayScore;
    const redCardCountSource = team == 'team1' ? 'homeRedCardCount' : 'awayRedCardCount';

    const teamName = getTeamNameHelper(
      locales,
      selectedLocale,
      match.competitors[team == 'team1' ? 0 : 1],
      t
    );
    const showServingBall =
      team == 'team1' ? homeServingBall && servingBall : awayShowServingBall && servingBall;
    return (
      <span>
        {teamName} {showServingBall}
        {match[redCardCountSource] > 0
          ? team == 'team1'
            ? homeRedCardCount
            : awayRedCardCount
          : null}
      </span>
    );
  };

  const getScore = (team) => {
    if (match.eventStatus == EnumEventStatus.RUNNING) {
      if (
        match.sport == sportTypeToSportId('Volleyball') ||
        match.sport == sportTypeToSportId('Tennis') ||
        match.sport == sportTypeToSportId('TableTennis')
      ) {
        if (props.desktop) {
          return (
            <span className="float-end text-primary ps-3">
              {team == 'team1' ? homeScores : awayScores}
            </span>
          );
        } else {
          return <span className="float-end">{team == 'team1' ? homeScores : awayScores}</span>;
        }
      } else {
        if (props.desktop) {
          if (match.matchStatusId == EnumMatchStatusName.HALFTIME) {
            return (
              <span className="float-end ps-3">
                {team == 'team1' ? match.homeScore : match.awayScore}
              </span>
            );
          } else {
            return (
              <span className="float-end text-primary ps-3">
                {team == 'team1' ? match.homeScore : match.awayScore}
              </span>
            );
          }
        } else {
          return (
            <span className="float-end">{team == 'team1' ? match.homeScore : match.awayScore}</span>
          );
        }
      }
    } else {
      return null;
    }
  };

  const getTeams = (team) => {
    const redCardCountSource = team == 'team1' ? 'homeRedCardCount' : 'awayRedCardCount';
    const teamName = getTeamNameHelper(
      locales,
      selectedLocale,
      match.competitors[team == 'team1' ? 0 : 1],
      t
    );
    const fallbackColor = isDesktop
      ? team == 'team1'
        ? 'white'
        : 'black'
      : team == 'team1'
        ? 'black'
        : 'white';

    return (
      <div className="d-flex align-items-center gap-1">
        <Jersey
          width={16}
          height={16}
          jersey={match.competitors[team == 'team1' ? 0 : 1].jersey}
          color={fallbackColor}
        />

        {match.sport == sportTypeToSportId('Volleyball') ||
          match.sport == sportTypeToSportId('Tennis') ||
          match.sport == sportTypeToSportId('TableTennis') ? (
          getTeam(team)
        ) : (
          <span>
            {teamName}
            {match[redCardCountSource] > 0
              ? team == 'team1'
                ? homeRedCardCount
                : awayRedCardCount
              : null}
          </span>
        )}
        <div className="ms-auto">{props.desktop ? null : getScore(team)}</div>
      </div>
    );
  };

  const getDesktopMatchCardMarkets = () => {
    const findOddsRowMarket = (markets, marketId, isLiveEvent = false) => {
      const pickSingleMarket = () => {
        const same = markets.filter((m) => m.id === marketId);
        if (same.length === 0) return undefined;
        return (
          same.find((m) => m.marketStatus === EnumMarketStatus.ACTIVE) ||
          same.find((m) => m.marketStatus !== 5) ||
          same[0]
        );
      };
      if (
        marketId === EnumMarketName.TOTAL_18 ||
        marketId === EnumMarketName.FIRSTHALFOVERUNDER_68
      ) {
        const sameMarkets = _.orderBy(
          markets.filter((m) => m.id === marketId),
          ['stringId'],
          ['asc']
        );
        if (isLiveEvent && marketId === EnumMarketName.FIRSTHALFOVERUNDER_68) {
          return sameMarkets[0];
        }
        const index = Math.floor((sameMarkets.length - 1) / 2);
        return sameMarkets[index];
      }
      if (
        marketId === EnumMarketName.HANDICAP_14 ||
        marketId === EnumMarketName.FIRSTHALFHANDICAP_65
      ) {
        // example result 0:1 or 1:0
        // use 0 or 2 character for comparison
        const oddsMarkets = _.orderBy(
          markets.filter((m) => m.id === marketId),
          ['stringId'],
          ['asc']
        );
        const homeHandicaps = oddsMarkets.filter((m) => m.stringId.split('-S:')[1][2] === '0');
        const awayHandicaps = oddsMarkets.filter((m) => m.stringId.split('-S:')[1][0] === '0');
        const diff = homeHandicaps.length - awayHandicaps.length;
        if (diff === 0 && homeHandicaps.length > 0 && awayHandicaps.length > 0) {
          const homeHandicapSmallestOdds = _.orderBy(
            homeHandicaps[0].outcomes,
            ['odds'],
            ['asc']
          )[0];
          const awayHandicapSmallestOdds = _.orderBy(
            awayHandicaps[0].outcomes,
            ['odds'],
            ['asc']
          )[0];
          return homeHandicapSmallestOdds.odds <= awayHandicapSmallestOdds.odds
            ? homeHandicaps[0]
            : awayHandicaps[0];
        }
        return diff > 0
          ? homeHandicaps[Math.abs(diff) === 1 || Math.abs(diff) === 2 ? 0 : Math.abs(diff) - 1]
          : awayHandicaps[Math.abs(diff) === 1 || Math.abs(diff) === 2 ? 0 : Math.abs(diff) - 1];
      }
      return pickSingleMarket();
    };
    return props.desktopMarkets?.map((marketId) => (
      <div className="bet-group" key={marketId}>
        <BetTitleAndBox
          market={findOddsRowMarket(match.markets, marketId)}
          marketId={marketId}
          matchId={match.providerId}
          sportType={match.sport}
          lockIfNoMarket={props.lockIfNoMarket}
          hideMarketLabel={props.hideMarketLabel}
          hideMarketNameInCard={true}
          marketCount={activeMarkets.length}
        />
      </div>
    ));
  };

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const wasFavorite = match.isFavorite;
    try {
      const result = await dispatch(
        toggleFavorite({
          gameNumber: match.providerId,
          sportType: match.sport
        })
      ).unwrap();


      if (!wasFavorite && result?.isFavorite === false) {
        showToaster(t('common.messages.favoriteLimitReached'), 'error');
        return;
      }

      if (result?.success) {
        const successMessage =
          result?.message ||
          (wasFavorite
            ? 'Removed from favorites'
            : 'Added to favorites');
        showToaster(successMessage, 'success');
      } else {
        showToaster(result?.message || 'Favorite limit of 10 reached.', 'error');
      }
    } catch (error) {
      showToaster(error?.message || 'Unable to update favorite', 'error');
    }
  };

  if (props.desktop) {
    return (
      <div className="card card-body desktop-match-card text-white border-2">
        <div className="time">
          <div className="d-flex flex-column lh-sm text-start fw-semibold">
            <small className=" fw-bold">{gameDateToMatchDay(match.gameDate)}</small>

            <small className="time">{gameDateToMatchTime(match.gameDate)}</small>
          </div>
          <div className="d-flex gap-1">
            <span onClick={() => props.onMatchTrackerSet(match)}>
              <img width="25" height="20" src={`${AWS_BUCKET_URL}/Icons/stadyum2.png`} />
            </span>
          </div>
        </div>
        <div
          className="teams-group"
          onClick={() => props.onMatchDetailIdSet && props.onMatchDetailIdSet(match.providerId)}
          style={{ cursor: 'pointer' }}>
          <div className="text-truncate d-flex justify-content-between">
            {getTeams('team1')}

            <span
              className={
                match.matchStatusId == EnumMatchStatusName.HALFTIME
                  ? 'float-end text-end'
                  : 'float-end text-end'
              }>
              {match.eventStatus == EnumEventStatus.RUNNING ? (
                match.sport === sportTypeToSportId('Football') ? (
                  getMatchTime(match, t)
                ) : (
                  <span className="time">
                    {t(`components.matchcard.time.byMatchStatusId.${match.matchStatusId}`)}
                  </span>
                )
              ) : (
                ''
              )}
              {getScore('team1')}
            </span>
          </div>
          <div className="text-truncate d-flex justify-content-between">
            {getTeams('team2')}
            <span>{getScore('team2')}</span>
          </div>
        </div>
        {match.eventStatus == EnumEventStatus.RUNNING ? (
          ''
        ) : gameDateToMatchDay(match.gameDate).localeCompare(t('today')) == 0 ? (
          <img
            width="30"
            height="30"
            style={{ marginTop: '1rem' }}
            src={`${AWS_BUCKET_URL}/Icons/liveTime.png`}
          />
        ) : (
          <img
            width="30"
            height="30"
            style={{ marginTop: '1rem' }}
            src={`${AWS_BUCKET_URL}/Icons/time.png`}
          />
        )}
        <div className="bets-wrapper">{getDesktopMatchCardMarkets()}</div>
        <div className="match-details-link-wrapper">
          <button
            className="btn btn-link text-decoration-none d-flex flex-nowrap"
            onClick={handleToggleFavorite}
            style={{ marginTop: '0.76rem' }}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill={match.isFavorite ? '#dc3545' : 'none'}
              stroke={match.isFavorite ? '#dc3545' : '#ffffff'}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={{
        pathname: '/match-detail',
        hash: `#${match.providerId}`
      }}>
      <div className="border-gray-300 px-2 pb-2 pt-0 rounded mb-1 overflow-hidden">
        <div className="d-flex justify-content-between mb-1 ms-n2">
          <div className="d-flex text-center lh-sm">
            {match.eventStatus > EnumEventStatus.NOTSTARTED ? (
              <span className="px-2 py-1 text-white fs-8 bg-success">
                {t('components.matchcard.live')}
              </span>
            ) : (
              <span className="px-2 py-1 bg-primary text-white fs-8 min-w-40">
                {gameDateToMatchDay(match.gameDate)}
              </span>
            )}
            {match.eventStatus > EnumEventStatus.NOTSTARTED ? (
              match.sport === sportTypeToSportId('Football') ? (
                <span className="px-2 py-1 rounded-end-5 bg-dark text-white fs-8">
                  {getMatchTime(match, t)}
                </span>
              ) : (
                <span className="px-2 py-1 rounded-end-5 bg-dark text-white fs-8">
                  {t(`components.matchcard.time.byMatchStatusId.${match.matchStatusId}`)}
                </span>
              )
            ) : (
              <span
                className={`px-2 py-1 rounded-end-5 text-white fs-8 ${match.bookingStatus === 2 ? 'will-be-live' : 'bg-dark'
                  }`}>
                {gameDateToMatchTime(match.gameDate)}
              </span>
            )}
          </div>
          <div className="text-end" onClick={handleToggleFavorite} style={{ cursor: 'pointer' }}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill={match.isFavorite ? '#dc3545' : 'none'}
              stroke={match.isFavorite ? '#dc3545' : '#6c757d'}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </div>
        </div>
        <Row>
          <div className="text-dark fs-7">
            {getTeams('team1')}
            {getTeams('team2')}
          </div>
        </Row>
        <div>
          {/* match card main markets */}
          {MatchCardGetTitle()}
        </div>
      </div>
    </Link>
  );
}
export default MatchCard;
